import {
    ReactNode, useCallback, useEffect, useMemo, useState,
} from 'react';
import { DateTime } from 'luxon';
import { isEmpty } from '@rainbow-modules/validation';
import { DatePickerModal, Option } from 'react-rainbow-components';
import { PickListGlobalStyle } from 'components/DatePickerFilter/styled';
import { getFormatter } from 'data/services/date/formatter';
import { StyledPicklist } from './styled';

interface PicklistValue {
    label?: string;
    name?: string | number;
    icon?: ReactNode;
    value?: any;
}

interface DatePickerFilterProps {
    value?: Date[];
    onChange?: (value: Date[]) => void;
    [key: string]: unknown;
}

const dateFormatter = getFormatter('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
});

const options = [
    {
        name: 'all',
        label: 'All Time',
    },
    {
        name: 'today',
        label: 'Today',
    },
    {
        name: 'next-24h',
        label: 'Next 24 hours',
    },
    {
        name: 'next-7d',
        label: 'Next 7 days',
    },
    {
        name: 'custom',
        label: 'Custom Date Range',
    },
];

const getOptionFromValue = (value: Date[]) => {
    if (isEmpty(value)) {
        return options[0];
    }
    const [from, to] = value.map((date) => DateTime.fromJSDate(date));

    const now = DateTime.now();
    const toDate = to ?? DateTime.now();

    const diffNow = Math.trunc(from.diff(now, 'days').days);
    const diff = toDate.diff(from, 'days').days;

    if (to && from.hasSame(to, 'day')) {
        return options[1];
    }

    if (diffNow === 0 && diff <= 1) {
        return options[2];
    }

    if (diffNow === 0 && diff <= 7) {
        return options[3];
    }

    const customOption = options[4];

    if (to) {
        return {
            ...customOption,
            label: `${dateFormatter.format(from.toJSDate())} - ${dateFormatter.format(to.toJSDate())}`,
        };
    }

    return customOption;
};

const ReminderDateFilter = ({
    value: valueInProps,
    onChange = () => {},
    ...rest
}:DatePickerFilterProps) => {
    const [activeOption, setActiveOption] = useState<PicklistValue | undefined>();
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const openModal = useCallback(() => setIsOpen(true), []);
    const closeModal = useCallback(() => setIsOpen(false), []);

    const handlePicklistChange = useCallback(
        (newValue: PicklistValue) => {
            const { name } = newValue;
            setActiveOption((
                activeOption?.name === newValue.name
                    ? { ...newValue, label: activeOption?.label }
                    : newValue
            ));

            if (name === 'all') {
                return onChange([]);
            }

            if (name === 'today') {
                const startOf = new Date();
                const endOf = new Date();
                startOf.setHours(0, 0, 0, 0);
                endOf.setHours(23, 59, 59, 0);
                return onChange([startOf, endOf]);
            }

            if (name === 'next-24h') {
                const from = DateTime.now();
                const to = from.plus({ day: 1 });
                return onChange([from.toJSDate(), to.toJSDate()]);
            }

            if (name === 'next-7d') {
                const from = DateTime.now();
                const to = from.plus({ days: 7 });
                return onChange([from.toJSDate(), to.toJSDate()]);
            }
            return openModal();
        },
        [activeOption, onChange, openModal],
    );

    const handleDatePickerChange = useCallback(
        (newValue: Date | Date[]) => {
            const newVal = Array.isArray(newValue) ? newValue : [newValue];
            if (newVal.length > 1) {
                const [from, to] = newVal;
                setActiveOption({
                    ...activeOption,
                    label: `${dateFormatter.format(from)} - ${dateFormatter.format(to)}`,
                });
                closeModal();
            }
            onChange(newVal);
        },
        [closeModal, onChange, activeOption],
    );

    const pickListOptions = useMemo(
        () => options.map((option) => <Option key={`date-picker-filter-option__${option.name}`} {...option} />),
        [],
    );

    useEffect(
        () => {
            if (!valueInProps || (isEmpty(valueInProps) && !activeOption)) {
                setActiveOption({ name: 'all', label: 'All Time' });
            } else {
                setActiveOption(getOptionFromValue(valueInProps));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [valueInProps],
    );

    return (
        <>
            <PickListGlobalStyle />
            <StyledPicklist
                onChange={handlePicklistChange}
                value={activeOption}
                {...rest}
            >
                {pickListOptions}
            </StyledPicklist>
            <DatePickerModal
                isOpen={isOpen}
                onRequestClose={closeModal}
                onChange={handleDatePickerChange}
                value={valueInProps}
                selectionType="range"
                title="Select Date(s)"
                borderRadius="semi-square"
            />
        </>
    );
};

export default ReminderDateFilter;
